import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { errorMessage } from './errors';

const LABEL = 'text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1';
const DEFAULT_SCOPES = ['contacts:read'];

// Name an API key and pick what it may do. `scopes` is the server's allowlist;
// `onSubmit({ name, scopes })` is async and throws on failure (shown inline).
export default function ApiKeyDialog({ open, scopes, onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState(DEFAULT_SCOPES);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(''); setPicked(DEFAULT_SCOPES.filter(s => scopes.includes(s))); setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (s, on) => setPicked(prev => (on ? [...prev, s] : prev.filter(x => x !== s)));

  const submit = async () => {
    if (!name.trim()) { setError('Give the key a name'); return; }
    if (picked.length === 0) { setError('Pick at least one scope'); return; }
    setSaving(true);
    try { await onSubmit({ name: name.trim(), scopes: scopes.filter(s => picked.includes(s)) }); }
    catch (e) { setError(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="api-key-dialog">
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>The key can only do what you tick here. It is shown once, right after you create it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label htmlFor="ak-name" className={LABEL}>Key name</label>
            <Input id="ak-name" data-testid="ak-name" value={name} maxLength={120} placeholder="e.g. Zapier integration"
              onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <span className={LABEL}>Scopes</span>
            <ul className="space-y-1" data-testid="ak-scopes">
              {scopes.map(s => (
                <li key={s}>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox data-testid={`ak-scope-${s}`} checked={picked.includes(s)} onCheckedChange={(c) => toggle(s, c === true)} />
                    <code className="text-xs">{s}</code>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          {error && <p role="alert" data-testid="ak-error" className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button type="button" data-testid="ak-create" onClick={submit} disabled={saving} className="btn-gold">{saving ? 'Creating…' : 'Create key'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
